import type { CSSProperties } from 'react';
import type { ClassicTheme } from '../../domain/classic-profile';
import {
  shadertoyId,
  shadertoyPreviewUrl,
} from '../../lib/shadertoy';

export function backgroundAssetUrl(url: string): string {
  try {
    return new URL(url).hostname === 'i.imgur.com'
      ? `../background-media.php?url=${encodeURIComponent(url)}`
      : url;
  } catch {
    return url;
  }
}

export function cssFontStack(font: string): string {
  const escapedFont = font.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `"${escapedFont}", Arial, sans-serif`;
}

function googleFontHref(font: string): string {
  return `https://fonts.googleapis.com/css2?family=${
    encodeURIComponent(font).replace(/%20/g, '+')
  }&display=swap`;
}

export function ensureGoogleFontLink(font: string): HTMLLinkElement | undefined {
  if (font.toLowerCase() === 'arial') return undefined;
  const existing = [...document.querySelectorAll<HTMLLinkElement>('link[data-modern-skyspace-font]')]
    .find(link => link.dataset.modernSkyspaceFont === font);
  if (existing) return existing;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = googleFontHref(font);
  link.dataset.modernSkyspaceFont = font;
  document.head.appendChild(link);
  return link;
}

export function profileCardBackground(theme: ClassicTheme): CSSProperties {
  const style: CSSProperties = { backgroundColor: theme.backgroundColor };
  const cornerPosition = theme.backgroundPosition.includes(' ');

  if (theme.backgroundKind === 'image' && theme.backgroundUrl) {
    style.backgroundImage = `url("${backgroundAssetUrl(theme.backgroundUrl)}")`;
    style.backgroundPosition = cornerPosition ? theme.backgroundPosition : 'center';
    style.backgroundRepeat = theme.backgroundPosition === 'tiled' ? 'repeat' : 'no-repeat';
    style.backgroundSize = theme.backgroundPosition === 'tiled'
      ? 'auto'
      : theme.backgroundPosition === 'contain' || cornerPosition
        ? 'contain'
        : 'cover';
  } else if (theme.backgroundKind === 'legacy-shadertoy' && theme.backgroundUrl) {
    const shaderId = shadertoyId(theme.backgroundUrl);
    if (shaderId) {
      style.backgroundImage = `url("${shadertoyPreviewUrl(shaderId)}")`;
      style.backgroundPosition = 'center';
      style.backgroundSize = 'cover';
    }
  } else if (theme.backgroundKind === 'color') {
    const colours = theme.backgroundColors.length
      ? theme.backgroundColors
      : [theme.backgroundColor];
    if (theme.backgroundColorMode === 'pattern') {
      style.backgroundImage = theme.backgroundPattern === 'checks'
        ? `conic-gradient(${colours[0]} 25%, ${colours[1] || colours[0]} 0 50%, ${colours[0]} 0 75%, ${colours[1] || colours[0]} 0)`
        : `repeating-linear-gradient(${theme.backgroundGradientAngle}deg, ${colours[0]} 0 8px, ${colours[1] || colours[0]} 8px 16px)`;
      if (theme.backgroundPattern === 'checks') style.backgroundSize = '20px 20px';
    } else if (theme.backgroundColorMode === 'gradient') {
      style.backgroundImage = `linear-gradient(${theme.backgroundGradientAngle}deg, ${colours.join(', ')})`;
    }
  }
  return style;
}
